import { Controller } from '@hotwired/stimulus'
import parsePhoneNumber from 'libphonenumber-js/max'
import { getDiagnoseFacilities } from '../api/common/diagnose_facilities'
import delay from 'lodash/delay'

const DELAY_TIME = 800

export default class extends Controller {
  static targets = [
    'main',
    'form',
    'conditionWantNextBtn',
    'conditionWantQuestionsNextBtn',
    'skipQuestion',
    'firstQuestion',
    'normalQuestion',
    'question',
    'occupantCurrentStatusNote',
    'occupantCurrentStatusDischargeText',
    'occupantCurrentStatusDischargeTextAll',
    'occupantCurrentStatusLivingaloneText',
    'occupantCurrentStatusLivingaloneTextAll',
    'chatInputModalNextButton',
    'chatInputModalNextButton2',
    'chatInputModalNextButton3',
    'chatInputModalNextButton4',
    'chatInputModalNextButton5',
    'chatInputModalNextButton6',
    'chatInputModalNextButton7',
    'chatInputModalNextButton8',
    'chatInputModalNextButton9',
    'chatInputModalNextButton10',
    'chatInputModalNextButton11',
    'chatInputModalNextButton12',
    'chatInputModalNextButton13',
    'priorityCheckboxRankDischargeDestination',
    'priorityCheckboxRankConditionWant',
    'priorityCheckboxText1',
    'priorityCheckboxText2',
    'medicalCareNighttimeText',
    'medicalCareNighttimeText2',
    'easingBudgetText',
    'facilityToursFavoriteNextBtn',
    'facilityToursFavoriteNoVisitNextBtn',
    'facilityToursFavoriteInput',
    'facilityToursFavoriteInputNoVisit',
    'prefectures',
    'prefecture',
    'additionalPrefectures',
    'additionalPrefecture',
    'additionalAreaModal',
    'residentRecordAreaPrefectures',
    'residentRecordAreaPrefecture',
    'cities',
    'city',
    'additionalCities',
    'additionalCitiesSelectedText',
    'chatInputAdditionalCityNextButton',
    'residentRecordAreaCities',
    'telInput',
    'telError',
    'footer',
    'submit',
    'text',
    'modal',
    'residentRecordAreaModal',
    'prefectureText',
    'cityText',
    'timeText',
    'tel',
    'leaveModal',
  ]

  static values = {
    abCategory: String,
    startTime: Number,
    finishTime: Number,
  }

  static classes = [
    'displayVisible',
    'displayHidden',
    'visibilityVisible',
    'visibilityHidden',
    'valid',
    'invalid',
  ]

  get controllerName () {
    return this.element.dataset.controller
  }

  get className () {
    return this.element.dataset.classname
  }

  connect () {
    // デバッグ用
    this.isDevelop = false

    window.addEventListener('beforeunload', this.openUnloadDialog)

    // 流入時刻を取得
    if (!this.hasStartTimeValue) {
      this.startTimeValue = (new Date()).getTime()
    }

    // コラムCTAから入力値を引き継ぐ場合は、該当質問の入力をスキップする
    const url = new URL(window.location.href)
    const params = url.searchParams

    if (params.has('occupant_current_status_id') && params.has('occupant_timing_id') && params.has('occupant_gender') && params.has('occupant_age')) {
      const currentStatus = this.formTarget.elements['inquiry[occupant_current_status_id]']
      const gender = this.formTarget.elements['inquiry[occupant_gender]']
      const age = this.formTarget.elements['inquiry[occupant_age]']
      const timing = this.formTarget.elements['inquiry[occupant_timing_id]']

      currentStatus.value = params.get('occupant_current_status_id')
      gender.value = params.get('occupant_gender')
      timing.value = params.get('occupant_timing_id')

      // 優先度の質問で値を使用するため設定する
      const currentRadio = Array.from(this.formTarget.elements['inquiry[occupant_timing_id]']).find(radio => radio.value === timing.value)
      const currentStatusTitle = currentRadio ? currentRadio.dataset.title : ''
      this.priorityCheckboxText1Targets[0].textContent = currentStatusTitle.replace(/です/g, '')

      // 入居希望者のご状況を教えてください
      this.nextChatWithChoseCurrentStatus(null, { advance: false, hide: false })

      const ageValue = Math.floor(params.get('occupant_age') / 10) * 10
      if (ageValue >= 60 && ageValue <= 90) {
        age.value = ageValue
      } else {
        age.value = -1
      }

      this.skipQuestionTargets.forEach(el => {
        if (el.dataset.skip !== 'true') {
          el.dataset.skip = 'true'
        }
      })
    }

    this.showNextChat(1)
  }

  setTime () {
    if (!this.hasFinishTime) {
      this.finishTimeValue = (new Date()).getTime()
    }
    const diffTime = this.finishTimeValue - this.startTimeValue
    const minutes = Math.floor(diffTime / (1000 * 60))
    const seconds = Math.floor((diffTime % (1000 * 60)) / 1000)

    if (this.hasStartTimeValue && this.hasFinishTimeValue && minutes && seconds) {
      if (this.hasTimeTextTarget) {
        this.timeTextTargets.forEach(target => {
          target.innerHTML = `現在回答を始めてから${minutes}分${seconds}秒が経過しましたが、残り1項目です。`
        })
      }
    } else {
      if (this.hasTimeTextTarget) {
        this.timeTextTargets.forEach(target => {
          target.innerHTML = '現在回答を始めてから約5分が経過しましたが、残り1項目です。'
        })
      }
    }
  }

  openUnloadDialog (e) {
    e.preventDefault()
    e.returnValue = ''
  }

  openLeaveModal () {
    // 非表示対象
    const skipConditions = [
      'current_facility_name',
      'facility_tours_favorite_no_visit',
      'facility_tours_favorite',
    ]

    // questionTargetsの中でstyle="display: block;"が付与されている一番最後の要素を取得
    const visibleQuestions = this.questionTargets.filter(el => getComputedStyle(el).display === 'block')
    const lastVisibleQuestion = visibleQuestions.length > 0 ? visibleQuestions[visibleQuestions.length - 1] : null
    const qElem = lastVisibleQuestion ? lastVisibleQuestion.closest('[data-condition]') : null
    const currentCondition = qElem ? qElem.dataset.condition : null
    if (skipConditions.includes(currentCondition)) return

    if (!this.isDevelop) {
      this.closeLeaveModal()
      this.leaveModalTarget.classList.remove(this.displayHiddenClass)
      const chatQuestionContainer = document.getElementById('chat_question_container_leave')
      const loader1 = document.getElementById('chat_loader_leave_1')
      const loader2 = document.getElementById('chat_loader_leave_2')
      const chatQuestion1 = document.getElementById('chat_question_leave_1')
      const chatQuestion2 = document.getElementById('chat_question_leave_2')

      delay(() => {
        chatQuestionContainer.classList.add(this.visibilityVisibleClass)
      }, 800)
      delay(() => {
        loader1.classList.add(this.displayHiddenClass)
        chatQuestion1.classList.add(this.displayVisibleClass)
      }, 1600)
      delay(() => {
        loader2.classList.remove(this.displayHiddenClass)
      }, 2400)
      delay(() => {
        loader2.classList.add(this.displayHiddenClass)
        chatQuestion2.classList.add(this.displayVisibleClass)
      }, 3200)
    } else {
      console.log('open modal')
    }
  }

  closeLeaveModal () {
    this.leaveModalTarget.classList.add(this.displayHiddenClass)
    const chatQuestionContainer = document.getElementById('chat_question_container_leave')
    const loader1 = document.getElementById('chat_loader_leave_1')
    const loader2 = document.getElementById('chat_loader_leave_2')
    const chatQuestion1 = document.getElementById('chat_question_leave_1')
    const chatQuestion2 = document.getElementById('chat_question_leave_2')
    chatQuestionContainer.classList.remove(this.visibilityVisibleClass)
    loader1.classList.remove(this.displayHiddenClass)
    chatQuestion1.classList.remove(this.displayVisibleClass)
    loader2.classList.add(this.displayHiddenClass)
    chatQuestion2.classList.remove(this.displayVisibleClass)
  }

  // チャット質問用
  hideCurrentChatInput (e, text) {
    const title = e.currentTarget.dataset.title
    const current = e.currentTarget.dataset.current
    const currentInput = document.getElementById(`chat_input_${current}`)
    currentInput.classList.add(this.displayHiddenClass)

    delay(() => {
      currentInput.insertAdjacentHTML('beforebegin', `
        <div class="${this.element.dataset.classname}::chat-answer-container">
        <div class="${this.element.dataset.classname}::chat-answer-read">既読</div>
        <div class="${this.element.dataset.classname}::chat-answer">${text || title}</div>
        </div>
      `)
    }, DELAY_TIME / 2)
  }

  showNextChat (step, delayTime) {
    let chatQuestionContainer, chatQuestionContainerParent, chatInput, nextDom

    const maxSteps = this.questionTargets.length

    while (step <= maxSteps) {
      chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
      chatQuestionContainerParent = document.getElementById(`step_${step}`)
      chatInput = document.getElementById(`chat_input_${step}`)
      nextDom = this.questionTargets[step - 1]
      if (!chatQuestionContainerParent || chatQuestionContainerParent.dataset.skip !== 'true') {
        break
      }
      step++
    }

    // 吹き出しを表示
    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME)

    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)

    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2) - 400)
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 2))
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2))
    })
    if (delayTime) {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
      }, delayTime)
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, delayTime + 400)
    } else {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
      }, DELAY_TIME * (chatQuestions.length + 2))
    }

    console.log(`next:${step}`)
  }

  showNextChatSlow (step, delayTime) {
    const chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
    const chatInput = document.getElementById(`chat_input_${step}`)
    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom = this.questionTargets[step - 1]

    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2) - 400 + (index > 0 ? 500 : 0))
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 2) + (index > 0 ? 500 : 0))
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2) + (index > 0 ? 500 : 0))
    })
    if (delayTime) {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
      }, delayTime)
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, delayTime + 400)
    } else {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
      }, DELAY_TIME * (chatQuestions.length + 2))
    }

    console.log(`next:${step}`)
  }

  showNextChatOnly (step) {
    let chatQuestionContainer, chatQuestionContainerParent, nextDom

    const maxSteps = this.questionTargets.length

    while (step <= maxSteps) {
      chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
      chatQuestionContainerParent = document.getElementById(`step_${step}`)
      nextDom = this.questionTargets[step - 1]
      if (!chatQuestionContainerParent || chatQuestionContainerParent.dataset.skip !== 'true') {
        break
      }
      step++
    }

    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const DELAY_TIME = 1000

    // 吹き出しのみのステップ
    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2) - 400 + (index > 0 ? 500 : 0))
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 2) + (index > 0 ? 500 : 0))
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2) + (index > 0 ? 500 : 0))
    })

    // 次の質問
    const targetStep = step * 1 + 1
    const chatQuestionContainer2 = document.getElementById(`chat_question_container_${targetStep}`)
    const chatInput2 = document.getElementById(`chat_input_${targetStep}`)
    const loaders2 = chatQuestionContainer2.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions2 = chatQuestionContainer2.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom2 = this.questionTargets[step]
    this.questionTargets[step].style.display = 'block'
    delay(() => {
      window.scroll({ top: nextDom2.offsetTop, left: 0, behavior: 'smooth' })
      chatQuestionContainer2.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME * 6)
    Array.from(chatQuestions2).forEach((elm, index) => {
      delay(() => {
        loaders2[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 7) - 400)
      delay(() => {
        chatQuestions2[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 7))
      delay(() => {
        window.scroll({ top: nextDom2.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 7))
    })
    delay(() => {
      chatInput2.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME * (chatQuestions2.length + 7))

    console.log(`next:${step}`)
  }

  showNextChatOnlyAndSlow (step) {
    const chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom = this.questionTargets[step - 1]
    const DELAY_TIME = 1000

    // 吹き出しのみのステップ
    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2) - 400 + (index > 0 ? 500 : 0))
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 2) + (index > 0 ? 500 : 0))
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2) + (index > 0 ? 500 : 0))
    })

    // 次の質問
    const targetStep = step * 1 + 1
    const chatQuestionContainer2 = document.getElementById(`chat_question_container_${targetStep}`)
    const chatInput2 = document.getElementById(`chat_input_${targetStep}`)
    const loaders2 = chatQuestionContainer2.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions2 = chatQuestionContainer2.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom2 = this.questionTargets[step]
    this.questionTargets[step].style.display = 'block'
    delay(() => {
      window.scroll({ top: nextDom2.offsetTop, left: 0, behavior: 'smooth' })
      chatQuestionContainer2.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME * 6)
    Array.from(chatQuestions2).forEach((elm, index) => {
      delay(() => {
        loaders2[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 7) - 400)
      delay(() => {
        chatQuestions2[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 7))
      delay(() => {
        window.scroll({ top: nextDom2.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 7))
    })
    delay(() => {
      chatInput2.classList.add(this.visibilityVisibleClass)
    }, (DELAY_TIME * (chatQuestions2.length + 7)) + 1500)

    console.log(`next:${step}`)
  }

  updateGtmEvent (e) {
    const selectedValue = e.target.value
    const newEvantName = e.target.getAttribute('data-gtm-event').replace(/yes/, 'no')
    if (selectedValue === '-1') {
      e.target.setAttribute('data-gtm-event', newEvantName)
    }
  }

  appendChatInputs (step) {
    const template = document.getElementById(`chat_template_${step}`)
    const clone = template.content.cloneNode(true)
    document.getElementById(`chat_append_${step}`).appendChild(clone)
  }

  enableCondition (condition) {
    const questions = this.formTarget.querySelectorAll('[data-skip="true"]')
    questions.forEach((question) => {
      if (question.dataset.condition === condition) {
        question.dataset.skip = 'false'
      }
    })
  }

  // チャット質問用
  nextChatWithScroll (e) {
    this.hideCurrentChatInput(e)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollSlow (e) {
    this.hideCurrentChatInput(e)
    this.showNextChatSlow(e.currentTarget.dataset.next)
  }

  nextChatWithChatOnlyAndSlow (e) {
    this.hideCurrentChatInput(e)
    this.showNextChatOnlyAndSlow(e.currentTarget.dataset.next, 4700)
  }

  // モーダル次へボタン:１つ以上選択
  chatInputModalNextButtonStatusSome (e) {
    const name = e.target.name
    const elements = Array.from(this.formTarget.elements[name])

    if (!elements.length) return

    const isChecked = elements.some(checkbox => checkbox.checked)

    const buttonMap = {
      'inquiry[extra_input_params][medical_care_types][]': this.chatInputModalNextButtonTarget,
      'inquiry[extra_input_params][dementia_symptoms_forgetfulness][]': this.chatInputModalNextButton2Target,
      'inquiry[extra_input_params][dementia_symptoms_other][]': this.chatInputModalNextButton3Target,
      'inquiry[extra_input_params][home_care_emergency_id][]': this.chatInputModalNextButton4Target,
      'inquiry[extra_input_params][home_care_normal_situation_id][]': this.chatInputModalNextButton5Target,
      'inquiry[extra_input_params][service_issues_current_facility_id][]': this.chatInputModalNextButton7Target,
      'inquiry[asset_types][]': this.chatInputModalNextButton8Target,
      'inquiry[extra_input_params][pension_amount_id]': this.chatInputModalNextButton9Target,
      'inquiry[extra_input_params][easing_budget_id]': this.chatInputModalNextButton10Target,
      'inquiry[extra_input_params][specific_diseases][]': this.chatInputModalNextButton13Target,
    }

    const button = buttonMap[name]
    if (button) {
      button.disabled = !isChecked
    }
  }

  // モーダル次へボタン:全選択
  chatInputModalNextButtonStatusEvery (e) {
    const name = e.target.name
    const elements = Array.from(this.formTarget.elements[name])

    if (!elements.length) return

    const isAllChecked = elements.every(checkbox => checkbox.checked)

    const buttonMap = {
      'inquiry[extra_input_params][condition_priority][]': this.chatInputModalNextButton11Target,
      'inquiry[extra_input_params][desired_discharge_destination][]': this.chatInputModalNextButton12Target,
    }

    const button = buttonMap[name]
    if (button) {
      button.disabled = !isAllChecked
    }
  }

  chatInputAdditionalCityNextButtonStatus (e) {
    // ほかの希望のエリア
    const checkedFlag = Array.from(this.formTarget.elements['inquiry[extra_input_params][additional_city_id][]']).some(function (checkbox) {
      return checkbox.checked
    })
    const selectedCities = Array.from(this.formTarget.elements['inquiry[extra_input_params][additional_city_id][]'])
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.dataset.city)
    this.additionalCitiesSelectedTextTarget.innerHTML = selectedCities.join(', ')
    this.chatInputAdditionalCityNextButtonTarget.disabled = !checkedFlag
  }

  setEasingBudget (e) {
    if (e.target.value === '6' || e.target.value === '7') {
      this.easingBudgetTextTarget.innerHTML = '15万円以下は人気の価格帯のため、ご希望の時期に入居できない可能性があります'
    }
  }

  setEasingBudgetB (e) {
    // 希望の月額料金によってテキスト変更
    if (e.target.value === '6' || e.target.value === '7') {
      this.easingBudgetTextTarget.innerHTML = '15万円以下は人気の価格帯のため、予算に幅を持たせる方も少なくありません'
    }
  }

  // ================================
  // 各質問固有の関数
  // ================================

  nextChatWithScrollCheckAnswerSecondChoiceArea (e) {
    // ほかにご希望のエリアはありますか？
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][second_choice_area]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === '1') {
      this.enableCondition('second_choice_area')
      this.showNextChat(e.currentTarget.dataset.next)
    } else {
      this.showNextChat(Number(e.currentTarget.dataset.next) + 2)
    }
  }

  // 希望の月額料金を教えてください
  nextChatWithScrollCheckAnswerDesiredMonthlyFee (e) {
    const currentValue = this.formTarget.elements['inquiry[expected_monthly_fee_id]'].value
    const currentRadio = Array.from(this.formTarget.elements['inquiry[expected_monthly_fee_id]']).find(radio => radio.value === currentValue)
    const currentStatusTitle = currentRadio ? currentRadio.dataset.title : ''
    this.priorityCheckboxText2Targets[0].textContent = currentStatusTitle.replace(/です/g, '')
    this.hideCurrentChatInput(e)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerOccupantCareLevelStatus (e) {
    // 入居希望者の介護度を教えてください
    const currentStatusValue = this.formTarget.elements['inquiry[extra_input_params][occupant_care_level_status_id]'].value
    this.hideCurrentChatInput(e)

    if (currentStatusValue === 'はい') {
      this.enableCondition('occupant_care_level_id')
      this.enableCondition('classification_change_id')
      this.enableCondition('care_insurance_burden_ratio_id')
    }
    if (currentStatusValue === '新規申請中') {
      this.enableCondition('occupant_care_level_id')
    }

    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerRefusalToEnteringFacilities (e) {
    // 入居希望者は施設に入ることを拒否する傾向はありますか？
    // NOTE: refusal_to_entering_facilities_id は Step 8.5系（認知症の症状）でも使用されている。
    // このフィールドを削除・変更する際は、nextChatWithScrollCheckAnswerDementiaSymptoms 側も合わせて修正すること。
    const value = this.formTarget.elements['inquiry[extra_input_params][refusal_to_entering_facilities_id]'].value
    this.hideCurrentChatInput(e)
    if (value === 'はい' || value === 'わからない') {
      this.enableCondition('avoided_care_service')
      this.enableCondition('facility_search_intention')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerDementiaSymptoms (e) {
    // 認知症の症状はありますか？
    const occupantDementiaValue = this.formTarget.elements['inquiry[occupant_dementia_id]'].value
    this.hideCurrentChatInput(e)

    if (occupantDementiaValue === '1') {
      this.enableCondition('dementia_symptoms_forgetfulness')
      this.enableCondition('dementia_symptoms_aggression')
      this.enableCondition('dementia_symptoms_harassment')
      this.enableCondition('dementia_symptoms_wandering')
      this.enableCondition('dementia_symptoms_other')
    }

    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerDementiaSymptomsSpecific (e) {
    // 具体的な認知症の症状も踏まえて、施設をお探ししますか？
    const dementiaSymptomsValue = this.formTarget.elements['inquiry[extra_input_params][dementia_symptoms]'].value
    this.hideCurrentChatInput(e)

    if (dementiaSymptomsValue === 'はい') {
      this.enableCondition('dementia_symptoms_forgetfulness')
      this.enableCondition('dementia_symptoms_aggression')
      this.enableCondition('dementia_symptoms_harassment')
      this.enableCondition('dementia_symptoms_wandering')
      this.enableCondition('dementia_symptoms_other')
    }

    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatForDementiaSymptomsForgetfulness (e) {
    // 認知症の「物忘れ」の症状を選択する
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][dementia_symptoms_forgetfulness][]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0
      ? names.length > 1
        ? names.join('<br>')
        : names[0]
      : 'ありません'

    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatForDementiaSymptomsOther (e) {
    // そのほか、伝えておきたい認知症の症状
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][dementia_symptoms_other][]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0 ? names.join('、') : 'ありません'

    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswer (e) {
    // 医療ケアは必要ですか？
    const currentStatusValue = this.formTarget.elements['inquiry[occupant_medical_care_required_id]'].value
    this.hideCurrentChatInput(e)

    if (currentStatusValue === '1') {
      this.enableCondition('medical_care_types')
      this.showNextChat(e.currentTarget.dataset.next, 3950)
    } else {
      this.showNextChat(e.currentTarget.dataset.next)
    }
  }

  nextChatForMedicalCare (e) {
    // 必要な医療ケア
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][medical_care_types][]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    // 夜間対応
    const additionalChat = ['インシュリン注射', 'たん吸引', '胃ろう', '鼻腔・経管栄養']
    const matchedMedicalCareValues = values.filter(val => additionalChat.includes(val))

    if (matchedMedicalCareValues.length === 1 && this.hasMedicalCareNighttimeText2Target) {
      // 要素が存在する場合のみremoveを実行
      this.medicalCareNighttimeText2Target?.remove()
    }

    if (matchedMedicalCareValues.length > 0 && this.hasMedicalCareNighttimeTextTarget) {
      const valueText = matchedMedicalCareValues
        .map(val => `「${val}」`)
        .join('、')
      this.medicalCareNighttimeTextTargets.forEach(target => {
        target.innerHTML = valueText
      })
    }

    const valueText = names.length > 0 ? names.join('、') : 'ありません'
    this.hideCurrentChatInput(e, valueText)

    if (matchedMedicalCareValues.length > 0) {
      this.enableCondition('medical_care_nighttime_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatForSpecificDiseases (e) {
    // 診断されている病気・症状を取得
    const selectedValues = []
    const selectedTitles = []

    this.formTarget.elements['inquiry[extra_input_params][specific_diseases][]'].forEach(element => {
      if (element.checked) {
        selectedValues.push(element.value)
        selectedTitles.push(element.dataset.title)
      }
    })

    const displayText = selectedTitles.length > 0 ? selectedTitles.join('、') : 'ありません'

    this.hideCurrentChatInput(e, displayText + 'です')
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithChoseCurrentStatus (e, { advance = true, hide = true } = {}) {
    // 入居希望者のご状況を教えてください
    const currentStatusValue = this.formTarget.elements['inquiry[occupant_current_status_id]'].value

    if (hide && e) this.hideCurrentChatInput(e)

    if (currentStatusValue === '4' || currentStatusValue === '5' || currentStatusValue === '6') {
      this.enableCondition('home_care_emergency_id')
      this.enableCondition('home_care_normal_situation_id')
      this.enableCondition('biggest_problem')
      this.enableCondition('how_to_spend_the_day')
      this.enableCondition('use_of_nursing_care_services')
      this.occupantCurrentStatusNoteTarget.style.display = 'none'
      this.occupantCurrentStatusLivingaloneTextAllTargets.forEach(elm => {
        elm.innerHTML = '入居希望者は一人暮らしですか？'
      })
    }
    // 病院
    if (currentStatusValue === '1') {
      this.enableCondition('discharge_instructions_id')
      this.occupantCurrentStatusLivingaloneTextTargets.forEach(elm => {
        elm.innerHTML = '入院'
      })
      this.occupantCurrentStatusDischargeTextTargets.forEach(elm => {
        elm.innerHTML = '退院'
      })
      this.occupantCurrentStatusDischargeTextAllTargets.forEach(elm => {
        elm.innerHTML = '退院時期を教えてください'
      })
      this.enableCondition('oral_intake_id')
    }
    // 老健
    if (currentStatusValue === '2') {
      this.enableCondition('deciding_when_to_leave_hospital')
      this.enableCondition('hospitalized_temporary_destination_id')
      this.occupantCurrentStatusDischargeTextTargets.forEach(elm => {
        elm.innerHTML = '退去'
      })
    }
    // 「それ以外の施設に入居」
    if (currentStatusValue === '3') {
      this.enableCondition('deciding_when_to_leave_hospital')
      this.enableCondition('hospitalized_temporary_destination_id')
      this.enableCondition('occupancy_period_id')
      this.enableCondition('current_facility_name')
      this.occupantCurrentStatusDischargeTextTargets.forEach(elm => {
        elm.innerHTML = '退去'
      })
    }

    if (advance && e) this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerDecidingWhenToLeaveHospital (e) {
    // 退去時期は決まっていますか？
    const currentStatusValue = this.formTarget.elements['inquiry[extra_input_params][deciding_when_to_leave_hospital]'].value
    // 入居希望者のご状況を教えてください
    const occupantCurrentStatusValue = this.formTarget.elements['inquiry[occupant_current_status_id]'].value
    this.hideCurrentChatInput(e)
    if (currentStatusValue === 'はい') {
      this.enableCondition('occupant_current_status_discharge_id')
      this.occupantCurrentStatusDischargeTextAllTargets.forEach(elm => {
        elm.innerHTML = '退去時期を教えてください'
      })
    }

    // 「それ以外の施設に入居・老健」：他施設への入居を検討している理由として、最も近しいものを1つ選んでくださいを表示する
    if (currentStatusValue === 'いいえ' && (occupantCurrentStatusValue === '3' || occupantCurrentStatusValue === '2')) {
      this.enableCondition('dissatisfaction_current_facility_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerDischargeInstructions (e) {
    // 医師からの退院指示を受けていますか
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][discharge_instructions_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === 'はい') {
      this.enableCondition('occupant_current_status_discharge_id')
      this.enableCondition('medical_insurance_limit_id')
    } else {
      this.enableCondition('hospitalization_status_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerHospitalizationStatus (e) {
    // 現在の入院状況を教えてください
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][hospitalization_status_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === '概ね回復/リハビリ中' || currentValue === '治療完了' || currentValue === 'わからない') {
      this.enableCondition('medical_insurance_limit_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerMedicalInsuranceLimit (e) {
    // 月額の医療保険に条件はありますか？
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][medical_insurance_limit_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === 'いいえ' || currentValue === 'わからない') {
      this.enableCondition('oral_intake_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerOccupantCurrentStatusLivingalone (e) {
    // 入居希望者は一人暮らしですか？
    const livingAloneValue = this.formTarget.elements['inquiry[occupant_current_status_home_id]']?.value
    this.hideCurrentChatInput(e)

    if (livingAloneValue === 'living_alone') {
      // 入居希望者のご状況を教えてください
      const currentStatusValue = this.formTarget.elements['inquiry[occupant_current_status_id]']?.value
      if (currentStatusValue === '4' || currentStatusValue === '5' || currentStatusValue === '6') {
        this.enableCondition('occupant_current_status_livingalone_detail')
      }
    }

    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerOralIntake (e) {
    // 口から栄養（食事）を摂取することはできますか？
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][oral_intake_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === 'はい') {
      this.enableCondition('intention_to_return_home_id')
    } else {
      this.enableCondition('use_of_gastric_fistula_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerIntentionToReturnHome (e) {
    // 在宅復帰の意向はありますか？
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][intention_to_return_home_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === '必ずしたい') {
      this.enableCondition('condition_before_hospitalization_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerUseOfGastricFistula (e) {
    // 胃ろうを利用していますか？
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][use_of_gastric_fistula_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === 'はい') {
      this.enableCondition('number_of_times_gastrostomy_used_id')
    } else {
      this.enableCondition('desired_discharge_destination')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerDissatisfactionCurrentFacility (e) {
    // 他施設への入居を検討している理由として、最も近しいものを1つ選んでください
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][dissatisfaction_current_facility_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === '現在の施設の料金が不満') {
      this.enableCondition('dissatisfaction_current_facility_fee_id')
    }
    if (currentValue === '現在の施設の対応・サービスが不満') {
      this.enableCondition('service_issues_current_facility_id')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  currentFacilityNameNext (e) {
    const input = this.formTarget.querySelector('[name="inquiry[extra_input_params][current_facility_name]"]')
    const value = input ? input.value.trim() : ''
    const valueText = value ? `${value}です` : '未入力です'
    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithScrollCheckAnswerSocialSecurityStatus (e) {
    // 生活保護の受給状況を確認する
    const currentValue = this.formTarget.elements['inquiry[occupant_social_security_status_id]'].value
    this.hideCurrentChatInput(e)

    if (currentValue === '1' || currentValue === '2') {
      this.showNextChat(e.currentTarget.dataset.next, 4700)
    } else {
      this.enableCondition('desire_for_welfare')
      this.enableCondition('asset_types')
      this.showNextChat(e.currentTarget.dataset.next)
    }
  }

  nextChatForServiceIssuesCurrentFacility (e) {
    // 「現在の施設の対応・サービスが不満」について、該当するものをすべて教えてください
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][service_issues_current_facility_id][]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0 ? names.join('、') : 'ありません'

    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatForHomeCareEmergency (e) {
    // 直近1か月以内に起こった出来事として、該当するものをすべて教えてください
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][home_care_emergency_id][]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0 ? names.join('、') : 'ありません'

    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatForHomeCareNormalSituation (e) {
    // 普段の介護のご状況として、該当するものをすべて教えてください
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][home_care_normal_situation_id][]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0 ? names.join('、') : 'ありません'

    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatForAsset (e) {
    // 資産として所有しているものを選択
    const assetTypesValues = []
    const assetTypesNames = []

    this.formTarget.elements['inquiry[asset_types][]'].forEach(elm => {
      if (elm.checked) {
        assetTypesValues.push(elm.value)
        assetTypesNames.push(elm.dataset.title)
      }
    })

    const valueText = assetTypesNames.length > 0 ? assetTypesNames.join('、') : 'ありません'
    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(Number(e.currentTarget.dataset.next), 4700)
  }

  nextChatForPensionAmount (e) {
    // 月の年金受給額を選択してください
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][pension_amount_id]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0 ? names.join('、') : 'ありません'

    this.hideCurrentChatInput(e, valueText + 'です')
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithMatchResidentRecord (e) {
    // 入居者の住民票
    const currentStatusValue = this.formTarget.elements['inquiry[match_resident_record_id]'].value

    this.hideCurrentChatInput(e)

    if (currentStatusValue === '0') {
      this.showNextChat(e.currentTarget.dataset.next)
    } else {
      this.showNextChat(Number(e.currentTarget.dataset.next) + 2)
    }
  }

  nextChatForEasingBudget (e) {
    // 超えたくない予算はいくらですか？
    const values = []
    const names = []

    this.formTarget.elements['inquiry[extra_input_params][easing_budget_id]'].forEach(elm => {
      if (elm.checked) {
        values.push(elm.value)
        names.push(elm.dataset.title)
      }
    })

    const valueText = names.length > 0 ? names.join('、') : 'ありません'

    this.hideCurrentChatInput(e, valueText + 'です')
    this.showNextChat(e.currentTarget.dataset.next)
  }

  selectedIndices = []

  updatePriorityNumberDischargeDestination () {
    const checkedIndices = this.getCheckedIndices('inquiry[extra_input_params][desired_discharge_destination][]')
    this.updateDisplay('inquiry[extra_input_params][desired_discharge_destination][]', 'priorityCheckboxRankDischargeDestination', checkedIndices)
  }

  setPriorityAnswerDischargeDestination (e) {
    this.setPriorityAnswer(e, 'inquiry[extra_input_params][desired_discharge_destination][]', 'priorityCheckboxRankDischargeDestination')
  }

  updatePriorityNumberConditionWant () {
    const checkedIndices = this.getCheckedIndices('inquiry[extra_input_params][condition_priority][]')
    this.updateDisplay('inquiry[extra_input_params][condition_priority][]', 'priorityCheckboxRankConditionWant', checkedIndices)
  }

  setPriorityAnswerConditionWant (e) {
    this.setPriorityAnswer(e, 'inquiry[extra_input_params][condition_priority][]', 'priorityCheckboxRankConditionWant')
  }

  getCheckedIndices (name) {
    if (!this.selectedIndicesByName) this.selectedIndicesByName = {}
    if (!this.selectedIndicesByName[name]) this.selectedIndicesByName[name] = []
    const selectedIndices = this.selectedIndicesByName[name]
    const checkboxes = Array.from(this.formTarget.querySelectorAll(`[name="${name}"]`))

    checkboxes.forEach((checkbox, index) => {
      if (checkbox.checked) {
        if (!selectedIndices.includes(index)) {
          selectedIndices.push(index)
        }
      } else {
        const idx = selectedIndices.indexOf(index)
        if (idx !== -1) {
          selectedIndices.splice(idx, 1)
        }
      }
    })
    return selectedIndices
  }

  updateDisplay (name, rankTargetName, checkedIndices) {
    const spans = this[`${rankTargetName}Targets`]
    const checkboxes = Array.from(this.formTarget.querySelectorAll(`[name="${name}"]`))

    checkboxes.forEach((checkbox, index) => {
      const originalValue = checkbox.dataset.originalValue || checkbox.value.split('-').pop()
      checkbox.dataset.originalValue = originalValue // 元の値を保存

      if (checkedIndices.includes(index)) {
        const rank = checkedIndices.indexOf(index) + 1
        spans[index].textContent = rank

        checkbox.value = `${rank}位${originalValue}`
      } else {
        spans[index].textContent = ''

        checkbox.value = originalValue
      }
    })
  }

  setPriorityAnswer (e, name, rankTargetName) {
    const values = []
    const names = []
    const checkboxes = this.formTarget.elements[name]
    const spans = this[`${rankTargetName}Targets`]
    Array.from(checkboxes).forEach((elm, index) => {
      if (elm.checked) {
        values.push(elm.value)
        names.push('(' + spans[index].textContent + '位)' + elm.dataset.title)
      }
    })
    const valueText = names.length > 0 ? names.join('<br>') + 'です' : 'ありません'
    this.hideCurrentChatInput(e, valueText)
    this.showNextChat(e.currentTarget.dataset.next, 3300)
  }

  // ほかに外せないこだわり条件があれば教えてください
  conditionWantNextBtnStatus (e) {
    const MAX_SELECTION = 5
    const checkedCount = Array.from(this.formTarget.elements['inquiry[extra_input_params][condition_want][]']).filter(function (radio) {
      return radio.checked
    }).length
    if (checkedCount > MAX_SELECTION) {
      e.target.checked = false
      alert('最大5つまでしか選択できません。')
      return
    }
    const checkedFlag = Array.from(this.formTarget.elements['inquiry[extra_input_params][condition_want][]']).some(function (checkbox) {
      return checkbox.checked
    })
    this.conditionWantNextBtnTarget.disabled = !checkedFlag
  }

  nextChatWithConditionWant (e) {
    const checkedTitles = []
    const checkedElements = Array.from(this.formTarget.elements['inquiry[extra_input_params][condition_want][]']).filter(el => el.checked)

    const condition = checkedElements[0].dataset.condition
    if (checkedElements.length === 1 && condition && condition === 'condition_want_gradation10') {
      this.hideCurrentChatInput(e, `${checkedElements[0].dataset.title}です`)
      this.showNextChat(e.currentTarget.dataset.next)
      this.nextChatWithSubmitBefore2(e)
      return
    }

    checkedElements.forEach((el, index) => {
      const condition = el.dataset.condition

      if (condition && condition !== 'condition_want_gradation10') {
        this.enableCondition(condition)
        this.enableCondition('condition_want_gradation_balloon')
      }

      if (index === 0) {
        // targetsの配列をコピーしてからremoveを実行
        Array.from(this.normalQuestionTargets).forEach(target => {
          const parent = target.closest('[data-condition]')
          if (parent && parent.dataset.condition === condition) {
            target.remove()
          }
        })
      } else {
        // targetsの配列をコピーしてからremoveを実行
        Array.from(this.firstQuestionTargets).forEach(target => {
          const parent = target.closest('[data-condition]')
          if (parent && parent.dataset.condition === condition) {
            target.remove()
          }
        })
      }
      checkedTitles.push(el.dataset.title)
    })

    const valueText = checkedTitles.join('、')
    this.hideCurrentChatInput(e, `${valueText}、です`)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithConditionWantQuestions (e) {
    this.hideCurrentChatInput(e, e.currentTarget.dataset.text)
    const currentCondition = e.currentTarget.dataset.condition
    const checkedElements = Array.from(this.formTarget.elements['inquiry[extra_input_params][condition_want][]'])
      .filter(el => el.checked && el.dataset.condition !== 'condition_want_gradation10')
    const lastCheckedElement = checkedElements[checkedElements.length - 1]

    if (lastCheckedElement && currentCondition === lastCheckedElement.dataset.condition) {
      this.showNextChatOnly(e.currentTarget.dataset.next)
      this.nextChatWithSubmitBefore2(e)
    } else {
      this.showNextChat(e.currentTarget.dataset.next)
    }
  }

  conditionWantQuestionsNextBtnStatus (e) {
    const currentCondition = e.currentTarget.dataset.condition
    const name = `inquiry[extra_input_params][${currentCondition}]`
    const elements = this.formTarget.elements[name]

    // クリックイベントが発生した時点でラジオボタンは選択されている
    // elementsが取得できない場合でもe.target.checkedで判定可能
    let checkedFlag = false
    if (elements) {
      const elementArray = elements instanceof NodeList || elements instanceof RadioNodeList ? Array.from(elements) : [elements]
      checkedFlag = elementArray.some(function (radio) {
        return radio.checked
      })
    } else {
      checkedFlag = e.target.checked
    }

    const targetElement = this.conditionWantQuestionsNextBtnTargets.find(target => target.dataset.condition === currentCondition)
    if (targetElement) {
      targetElement.setAttribute('data-text', e.target.dataset.title)
      targetElement.disabled = !checkedFlag
    }
  }

  // 入居の希望時期を教えてください
  nextChatWithScrollCheckAnswerTiming (e) {
    const currentValue = this.formTarget.elements['inquiry[occupant_timing_id]'].value
    const currentRadio = Array.from(this.formTarget.elements['inquiry[occupant_timing_id]']).find(radio => radio.value === currentValue)
    const currentStatusTitle = currentRadio ? currentRadio.dataset.title : ''
    this.priorityCheckboxText1Targets[0].textContent = currentStatusTitle.replace(/です/g, '')
    this.hideCurrentChatInput(e)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  // 1カ月以内に施設の見学に行かれましたか？
  nextChatWithScrollCheckAnswerRecentFacilityTours (e) {
    const currentValue = this.formTarget.elements['inquiry[extra_input_params][recent_facility_tours_id]'].value
    this.hideCurrentChatInput(e)
    if (currentValue === 'はい') {
      this.enableCondition('already_visited_reason_for_inquiry')
      this.enableCondition('facility_tours_favorite')
      this.enableCondition('facility_tours_number')
    } else if (currentValue === 'いいえ') {
      this.enableCondition('facility_tours_favorite_no_visit')
    }
    this.showNextChat(e.currentTarget.dataset.next)
  }

  enableNextButtonFacilityToursFavorite () {
    const input = this.facilityToursFavoriteInputTarget
    const checkbox = this.formTarget.elements['inquiry[extra_input_params][facility_tours_favorite_nothing]']

    // テキストボックスが入力されているか、チェックボックスがチェックされているか
    const hasInput = input && input.value.trim().length > 0
    const hasChecked = checkbox && checkbox.checked

    if (hasInput || hasChecked) {
      this.facilityToursFavoriteNextBtnTarget.disabled = false
    } else {
      this.facilityToursFavoriteNextBtnTarget.disabled = true
    }
  }

  enableNextButtonFacilityToursFavoriteNoVisit () {
    const input = this.facilityToursFavoriteInputNoVisitTarget
    const checkbox = this.formTarget.elements['inquiry[extra_input_params][facility_tours_favorite_nothing_no_visit]']

    // テキストボックスが入力されているか、チェックボックスがチェックされているか
    const hasInput = input && input.value.trim().length > 0
    const hasChecked = checkbox && checkbox.checked

    if (hasInput || hasChecked) {
      this.facilityToursFavoriteNoVisitNextBtnTarget.disabled = false
    } else {
      this.facilityToursFavoriteNoVisitNextBtnTarget.disabled = true
    }
  }

  nextChatFacilityToursFavorite (e) {
    const input = this.formTarget.elements['inquiry[extra_input_params][facility_tours_favorite]']
    const checkbox = this.formTarget.elements['inquiry[extra_input_params][facility_tours_favorite_nothing]']

    let valueText = ''
    const hasInput = input && input.value.trim().length > 0
    const hasChecked = checkbox && checkbox.checked

    if (hasInput && hasChecked) {
      valueText = `${input.value.trim()}・特に気になった施設はない`
    } else if (hasChecked) {
      valueText = '特に気になった施設はない'
    } else if (hasInput) {
      valueText = input.value.trim()
    } else {
      valueText = '未入力'
    }

    this.hideCurrentChatInput(e, valueText + 'です')
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatFacilityToursFavoriteNoVisit (e) {
    const input = this.formTarget.elements['inquiry[extra_input_params][facility_tours_favorite_no_visit]']
    const checkbox = this.formTarget.elements['inquiry[extra_input_params][facility_tours_favorite_nothing_no_visit]']

    let valueText = ''
    const hasInput = input && input.value.trim().length > 0
    const hasChecked = checkbox && checkbox.checked

    if (hasInput && hasChecked) {
      valueText = `${input.value.trim()}・特に気になっている施設はない`
    } else if (hasChecked) {
      valueText = '特に気になっている施設はない'
    } else if (hasInput) {
      valueText = input.value.trim()
    } else {
      valueText = '未入力'
    }

    this.hideCurrentChatInput(e, valueText + 'です')
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatPrefectures (e) {
    const regionId = e.target.value
    const nextStep = e.currentTarget.dataset.next
    this.hideCurrentChatInput(e)
    fetch(`/area/regions/${regionId}/prefectures.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(prefectures => {
        this.updatePrefectures(prefectures)
        this.showNextChat(nextStep)
      })
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'inquiry-chat-column', operation: 'fetch-prefectures' } })
        }
        console.error('Failed to fetch prefectures:', error)
      })
  }

  nextChatAdditionalPrefectures (e) {
    const regionId = e.target.value
    const nextStep = e.currentTarget.dataset.next
    this.hideCurrentChatInput(e)
    fetch(`/area/regions/${regionId}/prefectures.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(prefectures => {
        this.updateAdditionalPrefectures(prefectures)
        this.showNextChat(nextStep)
      })
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'inquiry-chat-column', operation: 'fetch-additional-prefectures' } })
        }
        console.error('Failed to fetch prefectures:', error)
      })
  }

  nextChatResidentRecordAreaPrefectures (e) {
    const regionId = e.target.value
    const nextStep = e.currentTarget.dataset.next
    this.hideCurrentChatInput(e)
    fetch(`/area/regions/${regionId}/prefectures.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(prefectures => {
        this.updateResidentRecordAreaPrefectures(prefectures)
        this.showNextChat(nextStep)
      })
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'inquiry-chat-column', operation: 'fetch-resident-record-area-prefectures' } })
        }
        console.error('Failed to fetch prefectures:', error)
      })
  }

  nextChatChoseCity (e) {
    this.modalTarget.classList.add(this.displayHiddenClass)
    if (this.hasCityTextTarget) {
      this.cityTextTargets.forEach(target => {
        target.innerHTML = e.target.dataset.city
      })
    }
    this.hideCurrentChatInput(e, `${this.prefectureTarget.innerHTML}${e.target.dataset.title}`)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatAdditionalCity (e) {
    this.additionalAreaModalTarget.classList.add(this.displayHiddenClass)

    const selectedCities = Array.from(this.formTarget.elements['inquiry[extra_input_params][additional_city_id][]'])
      .filter(checkbox => checkbox.checked)
      .map(checkbox => this.additionalPrefectureTarget.innerHTML + checkbox.dataset.city)

    this.hideCurrentChatInput(e, `${selectedCities.join(', ')}です`)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatChoseResidentRecordAreaCity (e) {
    this.residentRecordAreaModalTarget.classList.add(this.displayHiddenClass)
    this.hideCurrentChatInput(e, `${this.residentRecordAreaPrefectureTarget.innerHTML}${e.target.dataset.title}`)
    this.showNextChat(e.currentTarget.dataset.next)
  }

  nextChatWithSubmitBefore2 (e) {
    this.submitTarget.innerHTML = '残り2項目です'
    this.nextChatWithSubmit(e)
  }

  nextChatWithSubmitBefore1 (e) {
    this.submitTarget.innerHTML = '残り1項目です'
    this.hideCurrentChatInput(e)

    const step = Number(this.telTarget.dataset.step)
    this.appendChatInputs(step)
    this.showNextChat(step)
    this.nextChatWithSubmit(e)
  }

  nextChatWithSubmit (e) {
    this.footerTarget.classList.add(this.displayVisibleClass)
    delay(() => {
      window.scroll({
        top: this.footerTarget.offsetTop + this.footerTarget.clientHeight - window.innerHeight,
        left: 0,
        behavior: 'smooth',
      })
    }, 800)
  }

  submitForm () {
    window.removeEventListener('beforeunload', this.openUnloadDialog)

    // HTML5のフォームバリデーション
    if (!this.formTarget.checkValidity()) {
      const messages = Array.from(this.formTarget.elements)
        .filter(el => el.willValidate && el.validity && !el.validity.valid)
        .map(f => `${f.name || f.id || '(unnamed)'}: ${f.validationMessage}`)
      throw new Error(messages.join('\n'))
    }
  }

  showCitiesModal (e) {
    const prefectureId = e.target.value
    this.prefectureTarget.innerHTML = e.target.dataset.title
    if (this.hasPrefectureTextTarget) {
      this.prefectureTextTargets.forEach(target => {
        target.innerHTML = e.target.dataset.title
      })
    }
    this.modalTarget.classList.remove(this.displayHiddenClass)
    fetch(`/area/prefectures/${prefectureId}/cities.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(cities => this.updateCities(e, cities))
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'inquiry-chat-column', operation: 'fetch-cities' } })
        }
        console.error('Failed to fetch cities:', error)
      })
  }

  showAdditionalCitiesModal (e) {
    const prefectureId = e.target.value
    this.additionalPrefectureTarget.innerHTML = e.target.dataset.title
    this.additionalAreaModalTarget.classList.remove(this.displayHiddenClass)
    fetch(`/area/prefectures/${prefectureId}/cities.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(cities => this.updateAdditionalCities(e, cities))
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'inquiry-chat-column', operation: 'fetch-additional-cities' } })
        }
        console.error('Failed to fetch cities:', error)
      })
  }

  showResidentRecordAreaCitiesModal (e) {
    const prefectureId = e.target.value
    this.residentRecordAreaPrefectureTarget.innerHTML = e.target.dataset.title
    this.residentRecordAreaModalTarget.classList.remove(this.displayHiddenClass)
    fetch(`/area/prefectures/${prefectureId}/cities.json`)
      .then(response => {
        if (!response.ok) throw new Error(`HTTP Error: ${response.status}`)
        return response.json()
      })
      .then(cities => this.updateResidentRecordAreaCities(e, cities))
      .catch(error => {
        if (typeof Sentry !== 'undefined') {
          Sentry.captureException(error, { tags: { component: 'inquiry-chat-column', operation: 'fetch-resident-record-area-cities' } })
        }
        console.error('Failed to fetch cities:', error)
      })
  }

  openText () {
    this.textTarget.classList.remove(this.displayHiddenClass)
  }

  closeText () {
    this.textTarget.classList.add(this.displayHiddenClass)
  }

  getDiagnoseFacilityParams () {
    let params = {}
    let careLevelId = null
    let monthlyFeeMin = null
    let monthlyFeeMax = null
    let welfareAcceptable = null
    let dementiaAcceptable = null
    let diabetes = false
    const specialFeatureValues = []
    const medicalCareTypesValues = []

    // 入居希望者の介護度
    if (this.formTarget.elements['inquiry[occupant_care_level_id]'] &&
      Number(this.formTarget.elements['inquiry[occupant_care_level_id]'].value) >= 0 &&
      this.formTarget.elements['inquiry[occupant_care_level_id]'].value !== '30') {
      careLevelId = this.formTarget.elements['inquiry[occupant_care_level_id]'].value
    }

    // 月額予算
    switch (this.formTarget.elements['inquiry[expected_monthly_fee_id]'].value) {
      case '1':
        monthlyFeeMin = null
        monthlyFeeMax = 149999
        break
      case '2':
        monthlyFeeMin = 150000
        monthlyFeeMax = 199999
        break
      case '3':
        monthlyFeeMin = 200000
        monthlyFeeMax = 249999
        break
      case '4':
        monthlyFeeMin = 250000
        monthlyFeeMax = 299999
        break
      case '5':
        monthlyFeeMin = 300000
        monthlyFeeMax = null
        break
      default:
        monthlyFeeMin = null
        monthlyFeeMax = null
        break
    }

    // 必要な医療ケア
    this.formTarget.elements['inquiry[extra_input_params][medical_care_types][]'].forEach(elm => {
      if (elm.checked) {
        medicalCareTypesValues.push(elm.value)
      }
    })

    // こだわり条件
    this.formTarget.elements['inquiry[special_feature_names][]'].forEach(elm => {
      if (elm.checked) {
        specialFeatureValues.push(elm.value)
      }
    })

    // 認知症の症状
    if (this.formTarget.elements['inquiry[occupant_dementia_id]'] &&
      this.formTarget.elements['inquiry[occupant_dementia_id]'].value === '1') {
      dementiaAcceptable = this.formTarget.elements['inquiry[occupant_dementia_id]'].value
    }

    // 糖尿病の症状
    if (this.formTarget.elements['inquiry[occupant_diabetes]'] &&
      this.formTarget.elements['inquiry[occupant_diabetes]'].value === '1') {
      diabetes = true
    }

    // 生活保護の受給状況
    if (this.formTarget.elements['inquiry[occupant_social_security_status_id]'] &&
      this.formTarget.elements['inquiry[occupant_social_security_status_id]'].value === '1') {
      welfareAcceptable = this.formTarget.elements['inquiry[occupant_social_security_status_id]'].value
    }

    // こだわり条件の「認知症相談可」が選択されている場合は認知症の症状を「あり」に設定
    if (specialFeatureValues.includes('ninchisyo')) {
      dementiaAcceptable = '1'
    }

    // こだわり条件の「生活保護相談可」が選択されている場合は生活保護の受給状況を「あり」に設定
    if (specialFeatureValues.includes('seikatuhogo')) {
      welfareAcceptable = '1'
    }

    params = {
      prefecture_id: this.formTarget.elements['inquiry[prefecture_id]'] ? this.formTarget.elements['inquiry[prefecture_id]'].value : null,
      city_ids: this.formTarget.elements['inquiry[city_id]'] ? [this.formTarget.elements['inquiry[city_id]'].value] : null,
      monthly_fee_min: monthlyFeeMin,
      monthly_fee_max: monthlyFeeMax,
      welfare_acceptable: welfareAcceptable,
      dementia_acceptable: dementiaAcceptable,
      medical_care_id: diabetes ? '201' : null,
      care_level_id: careLevelId,
      special_features: specialFeatureValues,
      medical_care_types: medicalCareTypesValues,
    }

    return params
  }

  diagnoseFacilities () {
    const specialFeatureTexts = []

    this.formTarget.elements['inquiry[special_feature_names][]'].forEach(elm => {
      if (elm.checked) {
        specialFeatureTexts.push(elm.dataset.title)
      }
    })

    getDiagnoseFacilities(this.getDiagnoseFacilityParams()).then((data) => {
      console.log('diagnose success')
      console.log(data.results.length)
      if (data.results.length > 0) {
        // 診断結果が1件以上の場合
        if (this.hasResultQuestionBTarget) {
          this.resultQuestionBTarget.style.display = 'block'
        }
        if (this.hasDiagnoseCountTextTarget) {
          this.diagnoseCountTextTargets.forEach(target => {
            target.innerHTML = data.results.length
          })
        }
      } else {
        // 診断結果が0件の場合
        this.retryDiagnoseFacilities()
      }
    })
  }

  retryDiagnoseFacilities () {
    let paramsArea = {}
    let paramsFee = {}

    // 「エリアを広げる」の場合
    // 市区町村を設定しない
    paramsArea = this.getDiagnoseFacilityParams()
    delete paramsArea.city_ids

    paramsFee = this.getDiagnoseFacilityParams()
    // 「予算を広げる」の場合
    // 予算設定を削除
    paramsFee.monthly_fee_min = null
    paramsFee.monthly_fee_max = null

    getDiagnoseFacilities(paramsArea).then((dataArea) => {
      console.log('diagnose(no-area) success')
      console.log(dataArea.results.length)

      getDiagnoseFacilities(paramsFee).then((dataFee) => {
        console.log('diagnose(no-fee) success')
        console.log(dataFee.results.length)
        const count = dataArea.results.length + dataFee.results.length

        if (count > 0) {
          // 診断結果が1件以上の場合
          if (this.hasResultQuestionBTarget) {
            this.resultQuestionBTarget.style.display = 'block'
          }
          this.diagnoseCountTextTargets.forEach(target => {
            target.innerHTML = count
          })
        } else {
          // 診断結果が0件の場合
          if (this.hasResultQuestionATarget) {
            this.resultQuestionATarget.style.display = 'block'
          }
          console.log('non result')
        }
      })
    })
  }

  updateCities (e, cities) {
    const current = e.target.dataset.current
    const next = e.target.dataset.next
    const action = this.citiesTarget.dataset.nextAction
    const gtagEvent = this.citiesTarget.dataset.gtag
    const gtmEfCategory = this.citiesTarget.dataset.gtmEfCategory
    const gtmEventName = this.citiesTarget.dataset.gtmEventName
    this.citiesTarget.innerHTML =
      cities.map((city) => (
        `
          <li>
            <label>
              <input
                type="radio"
                name="inquiry[city_id]"
                value="${city.id}"
                class="inquiry-step-button"
                data-gtm-ef-category="${gtmEfCategory}"
                data-gtm-event="${gtmEventName}"
                data-title="${city.name}です"
                data-city="${city.name}"
                data-action="${action}"
                data-current="${current}"
                data-next="${next}"
                onClick="${gtagEvent}"
              />
              ${city.name}
            </label>
          </li>
      `)).join('')
  }

  updateAdditionalCities (e, cities, selectedValue) {
    const current = this.additionalCitiesTarget.dataset.current
    const next = this.additionalCitiesTarget.dataset.next
    const action = this.additionalCitiesTarget.dataset.nextAction
    const gtagEvent = this.additionalCitiesTarget.dataset.gtag
    const gtmEfCategory = this.additionalCitiesTarget.dataset.gtmEfCategory
    const gtmEventName = this.additionalCitiesTarget.dataset.gtmEventName
    this.additionalCitiesTarget.innerHTML =
      cities.map((city) => {
        const isSelected = Number(selectedValue) === city.id
        const className = isSelected ? 'selected' : ''
        return `
          <li>
            <label data-status="${className}">
              <input
                type="checkbox"
                name="inquiry[extra_input_params][additional_city_id][]"
                value="${city.id}"
                class="inquiry-step-button"
                data-gtm-ef-category="${gtmEfCategory}"
                data-gtm-event="${gtmEventName}"
                data-title="${city.name}です"
                data-city="${city.name}"
                data-action="${action}"
                data-current="${current}"
                data-next="${next}"
                onClick="${gtagEvent}"
              />
              <span>${city.name}</span>
            </label>
          </li>`
      }).join('')
  }

  updateResidentRecordAreaCities (e, residentRecordAreaCities) {
    const current = e.target.dataset.current
    const next = e.target.dataset.next
    const action = this.residentRecordAreaCitiesTarget.dataset.nextAction
    const gtagEvent = this.residentRecordAreaCitiesTarget.dataset.gtag
    const gtmEfCategory = this.residentRecordAreaCitiesTarget.dataset.gtmEfCategory
    const gtmEventName = this.residentRecordAreaCitiesTarget.dataset.gtmEventName
    this.residentRecordAreaCitiesTarget.innerHTML =
    residentRecordAreaCities.map((city) => (
        `
          <li>
            <label>
              <input
                type="radio"
                name="inquiry[resident_record_area_city_id]"
                value="${city.id}"
                class="inquiry-step-button"
                data-gtm-ef-category="${gtmEfCategory}"
                data-gtm-event="${gtmEventName}"
                data-title="${city.name}です"
                data-city="${city.name}"
                data-action="${action}"
                data-current="${current}"
                data-next="${next}"
                onClick="${gtagEvent}"
              />
              ${city.name}
            </label>
          </li>
      `)).join('')
  }

  updatePrefectures (prefectures) {
    const current = this.prefecturesTarget.dataset.current
    const next = this.prefecturesTarget.dataset.next
    const action = this.prefecturesTarget.dataset.nextAction
    const gtagEvent = this.prefecturesTarget.dataset.gtag
    const gtmEfCategory = this.prefecturesTarget.dataset.gtmEfCategory
    const gtmEventName = this.prefecturesTarget.dataset.gtmEventName
    const className = this.prefecturesTarget.dataset.className
    this.prefecturesTarget.innerHTML =
      prefectures.map(prefecture => (
        `
          <label class="${className}">
            <input
              type="radio"
              name="inquiry[prefecture_id]"
              value="${prefecture.id}"
              class="inquiry-step-button"
              data-gtm-ef-category="${gtmEfCategory}"
              data-gtm-event="${gtmEventName}"
              data-title="${prefecture.name}"
              data-next="${next}"
              data-current="${current}"
              data-action="${action}"
              onClick="${gtagEvent}"
            />
            <span>${prefecture.name}</span>
          </label>
        `
      )).join('')
  }

  updateAdditionalPrefectures (prefectures) {
    const current = this.additionalPrefecturesTarget.dataset.current
    const next = this.additionalPrefecturesTarget.dataset.next
    const action = this.additionalPrefecturesTarget.dataset.nextAction
    const gtagEvent = this.additionalPrefecturesTarget.dataset.gtag
    const gtmEfCategory = this.additionalPrefecturesTarget.dataset.gtmEfCategory
    const gtmEventName = this.additionalPrefecturesTarget.dataset.gtmEventName
    const className = this.additionalPrefecturesTarget.dataset.className
    this.additionalPrefecturesTarget.innerHTML =
      prefectures.map(prefecture => (
        `
          <label class="${className}">
            <input
              type="radio"
              name="inquiry[extra_input_params][additional_prefecture_id]"
              value="${prefecture.id}"
              class="inquiry-step-button"
              data-gtm-ef-category="${gtmEfCategory}"
              data-gtm-event="${gtmEventName}"
              data-title="${prefecture.name}"
              data-next="${next}"
              data-current="${current}"
              data-action="${action}"
              onClick="${gtagEvent}"
            />
            <span>${prefecture.name}</span>
          </label>
        `
      )).join('')
  }

  updateResidentRecordAreaPrefectures (residentRecordAreaPrefectures) {
    const current = this.residentRecordAreaPrefecturesTarget.dataset.current
    const next = this.residentRecordAreaPrefecturesTarget.dataset.next
    const action = this.residentRecordAreaPrefecturesTarget.dataset.nextAction
    const gtagEvent = this.residentRecordAreaPrefecturesTarget.dataset.gtag
    const gtmEfCategory = this.residentRecordAreaPrefecturesTarget.dataset.gtmEfCategory
    const gtmEventName = this.residentRecordAreaPrefecturesTarget.dataset.gtmEventName
    const className = this.residentRecordAreaPrefecturesTarget.dataset.className
    this.residentRecordAreaPrefecturesTarget.innerHTML =
      residentRecordAreaPrefectures.map(prefecture => (
        `
          <label class="${className}">
            <input
              type="radio"
              name="inquiry[resident_record_area_prefecture_id]"
              value="${prefecture.id}"
              class="inquiry-step-button"
              data-gtm-ef-category="${gtmEfCategory}"
              data-gtm-event="${gtmEventName}"
              data-title="${prefecture.name}"
              data-next="${next}"
              data-current="${current}"
              data-action="${action}"
              onClick="${gtagEvent}"
            />
            <span>${prefecture.name}</span>
          </label>
        `
      )).join('')
  }

  validateTel () {
    const value = this.telInputTarget.value
    if (!value) {
      this.telInputTarget.classList.remove(this.invalidClass, this.validClass)
      this.submitTarget.disabled = true
      this.telErrorTarget.style.display = 'none'
      document.getElementById('inquiry_is_sms_sendable_tel').value = 'false'
      return
    }
    const phoneNumber = parsePhoneNumber(value, 'JP')
    if (phoneNumber?.isValid()) {
      this.telInputTarget.classList.remove(this.invalidClass)
      this.telInputTarget.classList.add(this.validClass)
      this.submitTarget.disabled = false
      this.telErrorTarget.style.display = 'none'
      document.getElementById('inquiry_is_sms_sendable_tel').value = this.isSmsSendableTel(value).toString()
    } else {
      this.telInputTarget.classList.add(this.invalidClass)
      this.telInputTarget.classList.remove(this.validClass)
      this.submitTarget.disabled = true
      this.telErrorTarget.style.display = 'flex'
      document.getElementById('inquiry_is_sms_sendable_tel').value = 'false'
    }
  }

  toggleTelAndSubmit (isValid, telNumber) {
    if (isValid) {
      this.telInputTarget.classList.remove(this.invalidClass)
      this.telInputTarget.classList.add(this.validClass)
      this.submitTarget.disabled = false
      this.submitTarget.innerHTML = '問い合わせ（無料）'
      this.telErrorTarget.style.display = 'none'
      document.getElementById('inquiry_is_sms_sendable_tel').value = this.isSmsSendableTel(telNumber).toString()
    } else {
      this.telInputTarget.classList.add(this.invalidClass)
      this.telInputTarget.classList.remove(this.validClass)
      this.submitTarget.disabled = true
      this.telErrorTarget.style.display = 'flex'
    }
  }

  validatePhoneNumber () {
    const value = this.telInputTarget.value
    if (!value) {
      this.telInputTarget.classList.remove(this.invalidClass, this.validClass)
      this.submitTarget.disabled = true
      this.telErrorTarget.style.display = 'none'
      document.getElementById('inquiry_is_sms_sendable_tel').value = 'false'
      return
    }
    const phoneNumber = parsePhoneNumber(value, 'JP')
    this.toggleTelAndSubmit(phoneNumber?.isValid(), value)
  }

  isSmsSendableTel (rawPhoneNumber) {
    const regex = /^(070|080|090)/
    return regex.test(rawPhoneNumber)
  }
}
